// GR01 엑셀 업로드 — 1.0 TOOL_IMP 파리티(CommonUtil.onImportExcel: 클라 파싱 → 그리드 반영 → [저장]).
// 서버 업로드 API 는 1.0 에도 2.0 에도 없다. xlsx 를 exceljs 로 파싱해 한글 헤더를 컬럼 키로 매핑하고
// 각 행을 신규 행으로 반환한다. 사용자가 검토·선택한 뒤 [저장] 이 POST /api/v1/request-inbound 로 보낸다.
import ExcelJS from 'exceljs'
import { ERROR_CELL_DISPLAY_MAX, ERROR_ROW_DISPLAY_MAX, EXPORT_COLUMNS } from './consts'
import { NIKE_IDENTIFIER_FIELDS } from '../queries/requiredFields'

/** 업로드 반영 대상 — 저장 payload 에 실리는 컬럼만 받는다. 생성/수정 이력 컬럼은 무시한다 */
const IMPORTABLE_KEYS = new Set([
  'SPR_NM',
  'PLN_DATE',
  'ORD_OPT_NO',
  'ORD_TYPE',
  'LOT_ATTR5',
  'BRD_NM',
  'GDS_GROUP1',
  'GDS_NM',
  'STL_NO',
  'ORD_QTY',
  'AP_PRICE',
  'WH_NM',
  'WH_AREA',
  'BARCODE',
  ...NIKE_IDENTIFIER_FIELDS.map((field) => field.key),
  'TRS_INVOICE_NO',
  'COM_NM',
])

const NUMERIC_KEYS = new Set(['ORD_QTY', 'AP_PRICE'])
const IDENTIFIER_LABELS = new Map<string, string>(
  NIKE_IDENTIFIER_FIELDS.map((field) => [String(field.key), field.label]),
)

const titleToKey = new Map<string, string>(EXPORT_COLUMNS.map((c) => [c.title, c.key]))
const keyToTitle = new Map<string, string>(EXPORT_COLUMNS.map((c) => [c.key, c.title]))

export interface ImportedRow {
  [key: string]: string | number
}

/** 셀 값 → 글자. 수식·서식·하이퍼링크 등 복합값을 한 갈래로 모은다 (테스트에서 직접 부른다) */
export const cellText = (raw: ExcelJS.CellValue): string => {
  if (raw == null) return ''
  if (raw instanceof Date) {
    const pad2 = (n: number) => String(n).padStart(2, '0')
    return `${raw.getFullYear()}-${pad2(raw.getMonth() + 1)}-${pad2(raw.getDate())}`
  }
  // 서식이 들어간 셀은 CellRichTextValue 다. 먼저 걸러야 [object Object] 가 되지 않는다.
  if (typeof raw === 'object' && 'richText' in raw) {
    return (raw as { richText: { text: string }[] }).richText.map((part) => part.text).join('')
  }
  if (typeof raw === 'object' && 'text' in raw) return String((raw as { text: unknown }).text)
  // 수식 셀(CellFormulaValue)의 result 는 또 하나의 CellValue 다 — Date·richText·중첩 수식이 올 수 있다.
  // String() 으로 바로 찍으면 =TODAY() 같은 정상 날짜가 'Fri Jul 04 2026 …' 이 되어 8자리 판정에 걸리고,
  // 업로드가 "입고예정일자를 읽을 수 없는 행" 으로 거절된다. 같은 함수를 다시 태워 세 갈래를 함께 덮는다.
  if (typeof raw === 'object' && 'result' in raw) return cellText((raw as { result: ExcelJS.CellValue }).result ?? '')
  return String(raw)
}

/** ExcelJS가 Number로 읽은 식별자는 문자열화 전에 안전 정수인지 확인한다. 수식 결과도 같은 규칙이다. */
const isUnsafeIdentifierNumber = (raw: ExcelJS.CellValue): boolean => {
  if (typeof raw === 'number') return !Number.isSafeInteger(raw)
  if (typeof raw === 'object' && raw != null && 'result' in raw) {
    return isUnsafeIdentifierNumber((raw as { result: ExcelJS.CellValue }).result ?? '')
  }
  return false
}

/** 직접 오류 셀과 수식 결과의 오류를 같은 경로로 찾아 `[object Object]` 저장을 막는다. */
const cellError = (raw: ExcelJS.CellValue): string | null => {
  if (typeof raw !== 'object' || raw == null || raw instanceof Date) return null
  if ('error' in raw) return String((raw as { error: unknown }).error)
  if ('result' in raw) return cellError((raw as { result: ExcelJS.CellValue }).result ?? '')
  return null
}

/**
 * 'yyyy-MM-dd' 로 정규화한다. 못 읽으면 null.
 *
 * 자릿수만 보면 안 된다. 미국식 `07/04/2026` 은 구분자를 떼면 정확히 8자리라 자릿수 검사를
 * 통과하고, 앞 네 자리가 연도가 되어 `0704-20-26` 으로 들어간다. 형식이 틀린 게 아니라
 * **자릿수만 맞는 다른 형식**이라 8자리 관문이 못 잡는다.
 *
 * 그 값이 저장으로 가면 BE plannedDate 가 @NotNull 이 아니라 400 도 안 나고 그대로 들어가서,
 * 사용자는 자기가 적은 날짜로 저장된 줄 안다. 그래서 연도·월·일 범위를 다 보고, 실제 달력에
 * 있는 날인지까지 되돌려 확인한다(2026-02-31 같은 값을 거른다).
 */
const normalizeDate = (text: string): string | null => {
  const digits = text.replace(/[^0-9]/g, '')
  if (digits.length !== 8) return null

  const [year, month, day] = [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)].map(Number)
  if (year < 2000 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return null

  const parsed = new Date(year, month - 1, day)
  const sameDay = parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day
  return sameDay ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}` : null
}

/**
 * 거래유형 칸을 원장 코드로 되돌리는 함수를 만든다. 표시명(온라인/오프라인)과 원장 코드(MSSB2B 등)를
 * 둘 다 받는다.
 *
 * 표시명만 받으면 안 된다. 서버 다운로드 양식의 거래유형 칸에는 표시명이 아니라 원장 코드가 실린다
 * (moms-be NormalInboundExcelDto.transactionType ← ArrivalNoticeJpaRepository 의 lotAttr5). 그 파일을
 * 그대로 다시 올리는 것이 이 화면의 정상 경로인데, 코드를 못 알아보면 그 행이 전부 거절돼 한 행도
 * 반영되지 않는다(리뷰 반영).
 *
 * 같은 표시명이 둘 이상이면 그 표시명은 빼고 만든다. Map 은 뒤엣것이 앞엣것을 덮어 조용히 다른 코드로
 * 매핑되는데, 어느 쪽이 맞는지 알 방법이 없으니 매핑 불가로 보고 행을 거절하는 편이 안전하다. 표시명이
 * 겹치지 않는다는 불변조건은 화면 상수 시절 consts.test.ts 가 잠갔지만 값이 BE 데이터로 바뀌면서
 * 테스트로는 못 잠근다(리뷰 반영). 원장 코드로 적힌 칸은 이 경우에도 그대로 통과한다 — 코드는 모호하지 않다.
 */
const makeTransactionMapper = (options: { value: string; label: string }[]): ((text: string) => string | undefined) => {
  const labels = options.map((o) => o.label)
  const byLabel = new Map(options.filter((o) => labels.indexOf(o.label) === labels.lastIndexOf(o.label)).map((o) => [o.label, o.value]))
  const byValue = new Set(options.map((o) => o.value))
  return (text) => byLabel.get(text) ?? (byValue.has(text) ? text : undefined)
}

/** 읽을 수 없는 칸의 행 번호를 모아 두는 자리 — 저장 전에 어느 행이 왜 막혔는지 한 번에 안내한다 */
interface CellContext {
  /** 거래유형 표시명·원장 코드를 원장 코드로 되돌린다. 못 되돌리면 undefined */
  toTransactionValue: (text: string) => string | undefined
  badTransactionRows: number[]
  badDateRows: number[]
}

/**
 * 값이 있는 셀 하나를 컬럼 키에 맞춰 읽는다. 읽을 수 없는 칸은 행 번호를 `ctx` 에 모아 두고
 * undefined 를 돌려준다(거래유형만 해당 — 입고예정일자는 원문을 남겨 화면에서 보이게 한다).
 *
 * 셀 단위 분기를 행 순회 콜백 안에 두면 그 콜백 하나가 인지 복잡도 상한을 넘는다. 자리만 옮긴 것이고
 * 판단은 그대로다.
 */
const readCellValue = (key: string, text: string, rowNumber: number, ctx: CellContext): string | number | undefined => {
  if (NUMERIC_KEYS.has(key)) {
    // 숫자로 못 읽으면 원문을 그대로 둔다. 0 으로 조용히 바꾸면 어느 행이 잘못됐는지
    // 사용자가 알 수 없다. 저장 전 필수값 검사가 "수량이 숫자가 아닙니다" 로 짚어준다.
    const parsed = Number(text.replaceAll(',', ''))
    return Number.isNaN(parsed) ? text : parsed
  }
  if (key === 'LOT_ATTR5') {
    const mapped = ctx.toTransactionValue(text)
    if (mapped === undefined) ctx.badTransactionRows.push(rowNumber)
    return mapped
  }
  if (key === 'PLN_DATE') {
    // yyyyMMdd·yyyy/MM/dd 등 어떤 표기로 와도 저장 시 yyyyMMdd 로 정규화된다. 화면 표시는 yyyy-MM-dd 로 맞춘다.
    const normalized = normalizeDate(text)
    if (!normalized) ctx.badDateRows.push(rowNumber)
    return normalized ?? text
  }
  return text
}

/**
 * 거래유형(LOT_ATTR5) 옵션 — GET /api/v1/combos 응답(호출부에서 주입, LOGISPRD-12385).
 * 원장 코드로 되돌리는 규칙은 makeTransactionMapper 에 적어 두었다.
 *
 * 기본값을 두지 않는다 — 콤보 조회가 실패하거나 LOT_ATTR5 가 아직 비어 있으면 이 목록도 비고,
 * 그 상태에서 표시명이 원장 코드로 못 바뀌면 엑셀 원문("오프라인" 같은 한글)이 그대로 저장
 * body 의 lot5 로 나간다(리뷰 반영). 호출부가 옵션을 빼먹는 경로 자체를 타입으로 막는다.
 */
export const parseInboundExcel = async (
  file: File,
  transactionTypeOptions: { value: string; label: string }[],
  // NIKE_IDENTIFIERS_FLAG 값. 꺼지면 UID·옵션코드 헤더를 복원 전처럼 무시한다.
  nikeIdentifiers = true,
): Promise<ImportedRow[]> => {
  // 옵션이 비면 표시명·원장 코드 어느 쪽도 못 되돌린다 — 콤보 조회 실패 때 전 행을 거절하는 경로는
  // 그대로 닫혀 있다.
  const toTransactionValue = makeTransactionMapper(transactionTypeOptions)
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(await file.arrayBuffer())
  const ws = wb.worksheets[0]
  if (!ws) return []

  // 1행 = 한글 헤더. 서버 다운로드 양식(NormalInboundExcelDto)의 헤더와 같은 이름을 쓴다.
  const headerKeys: (string | null)[] = []
  ws.getRow(1).eachCell({ includeEmpty: true }, (cell, col) => {
    const key = titleToKey.get(cellText(cell.value).trim()) ?? null
    headerKeys[col] = key && IMPORTABLE_KEYS.has(key) && (nikeIdentifiers || !IDENTIFIER_LABELS.has(key)) ? key : null
  })
  if (!headerKeys.some(Boolean)) {
    throw new Error('헤더를 인식할 수 없습니다. 엑셀 다운로드 양식과 동일한 1행 헤더가 필요합니다.')
  }

  const rows: ImportedRow[] = []
  // 입고예정일자를 8자리로 못 읽은 행. 그대로 두면 저장 때 plannedDate 가 null 로 나가는데,
  // BE 는 그 필드가 @NotNull 이 아니라 400 을 주지 않고 당일 컷오프 일자로 대체한다
  // (InboundRequestMapperImpl.kt:80). 사용자는 자기가 적은 날짜로 들어간 줄 알고
  // 실제로는 오늘 차수로 들어간다. 업로드 시점에 막는다 (결정 09).
  const badDateRows: number[] = []
  // 거래유형이 표시명으로도 원장 코드로도 옵션에 없는 행. 조용히 원문(한글 표시명)을 lot5 로
  // 흘려보내면 원장이 "MSSB2B"·"" 가 아니라 "오프라인" 같은 값을 그대로 받는다(리뷰 반영).
  // 콤보 조회 실패나 LOT_ATTR5 미등록으로 옵션이 비어도 이 경로로 잡힌다.
  const badTransactionRows: number[] = []
  // ExcelJS는 숫자 셀을 JavaScript Number로 읽는다. 안전 정수 범위를 넘으면 cellText 전에 이미
  // 반올림되어 원문 복원이 불가능하므로, 잘못된 식별자를 저장하지 않고 텍스트 셀로 재입력시킨다.
  const unsafeIdentifierCells: string[] = []
  // 직접 오류 셀과 수식 결과 오류는 식별자뿐 아니라 모든 업로드 대상 컬럼에서 차단한다.
  // ExcelJS 오류 객체가 [object Object] 문자열로 저장되는 것을 막는다.
  const formulaErrorCells: string[] = []
  const cellContext: CellContext = {
    toTransactionValue,
    badTransactionRows,
    badDateRows,
  }
  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return
    const item: ImportedRow = {}
    let hasValue = false
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const key = headerKeys[col]
      if (!key) return
      const error = cellError(cell.value)
      if (error) {
        formulaErrorCells.push(`${rowNumber}행 ${keyToTitle.get(key) ?? key}: ${error}`)
        hasValue = true
        return
      }
      const identifierLabel = IDENTIFIER_LABELS.get(key)
      if (identifierLabel && isUnsafeIdentifierNumber(cell.value)) {
        unsafeIdentifierCells.push(`${rowNumber}행 ${identifierLabel}`)
        hasValue = true
        return
      }
      const text = cellText(cell.value).trim()
      if (!text) {
        // 숫자 칸이 비면 키를 빼지 않고 빈 문자열을 넣는다. 키가 없으면 신규 행 기본값(ORD_QTY: 0)이
        // 채워지는데 isBlank(0) 이 false 라 필수값 검사를 그냥 통과한다. 수량만 새어나가던 자리다.
        if (NUMERIC_KEYS.has(key)) item[key] = ''
        return
      }
      hasValue = true
      const value = readCellValue(key, text, rowNumber, cellContext)
      if (value !== undefined) item[key] = value
    })
    if (hasValue) rows.push(item)
  })

  const rowsSummary = (numbers: number[]) => {
    const shown = numbers.slice(0, ERROR_ROW_DISPLAY_MAX).join(', ')
    const more = numbers.length > ERROR_ROW_DISPLAY_MAX ? ` 외 ${numbers.length - ERROR_ROW_DISPLAY_MAX}개 행` : ''
    return `${shown}행${more}`
  }
  const errors: string[] = []
  if (formulaErrorCells.length) {
    errors.push(
      `엑셀 오류가 있는 셀이 있습니다 (${formulaErrorCells.slice(0, ERROR_CELL_DISPLAY_MAX).join(', ')}). 수식을 수정하거나 값으로 붙여넣어 다시 올려주세요.`,
    )
  }
  if (unsafeIdentifierCells.length) {
    errors.push(
      `UID·옵션코드는 큰 숫자를 정확히 읽을 수 없습니다 (${unsafeIdentifierCells.slice(0, ERROR_CELL_DISPLAY_MAX).join(', ')}). 해당 셀을 텍스트 형식으로 바꿔 다시 올려주세요.`,
    )
  }
  if (badDateRows.length) {
    errors.push(`입고예정일자를 읽을 수 없는 행이 있습니다 (${rowsSummary(badDateRows)}). yyyy-MM-dd 또는 yyyyMMdd 로 고쳐 다시 올려주세요.`)
  }
  if (badTransactionRows.length) {
    // 옵션 목록이 비어 있으면(콤보 조회 실패 등) 전 행이 여기 걸린다 — "다시 시도" 안내가 그 경우도 덮는다.
    errors.push(`거래유형을 읽을 수 없는 행이 있습니다 (${rowsSummary(badTransactionRows)}). 엑셀 다운로드 양식의 값을 그대로 쓰거나, 옵션 목록을 다시 불러온 뒤 다시 시도해주세요.`)
  }
  // message.error 는 단일 줄 토스트다 — 개행이 아니라 공백으로 이어 붙인다.
  if (errors.length) throw new Error(errors.join(' '))
  return rows
}
